import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, PenLine, Send, RefreshCw, Download, FileSignature, X, CheckCircle2, Clock, Copy, Upload, BarChart3, Timer, TrendingUp, Award } from 'lucide-react';
import { esign, letters, matters as mattersApi } from '@/lib/api';
import { toast } from 'sonner';
import { safeHtml } from '@/lib/sanitize';

const STATUS_TONE = {
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  signed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  cancelled: 'border-slate-200 bg-slate-50 text-slate-500',
};

const STATUS_ICON = {
  pending: Clock, signed: CheckCircle2, cancelled: X,
};

export default function EsignPage({ matterRef: initialMatterRef = null, embedded = false }) {
  const [items, setItems] = React.useState([]);
  const [counts, setCounts] = React.useState({ total: 0, pending: 0, signed: 0 });
  const [loading, setLoading] = React.useState(true);
  const [matterFilter, setMatterFilter] = React.useState(initialMatterRef || 'all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [matters, setMatters] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [detail, setDetail] = React.useState(null); // full esign object
  const [analytics, setAnalytics] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    const opts = {};
    if (matterFilter !== 'all') opts.matter_ref = matterFilter;
    if (statusFilter !== 'all') opts.status_filter = statusFilter;
    esign.list(opts).then((r) => { setItems(r.items || []); setCounts(r.counts || {}); })
      .finally(() => setLoading(false));
  }, [matterFilter, statusFilter]);

  const loadAnalytics = React.useCallback(() => {
    esign.analytics(90).then(setAnalytics).catch(() => setAnalytics(null));
  }, []);

  React.useEffect(load, [load]);
  React.useEffect(() => {
    mattersApi.list().then(setMatters).catch(() => setMatters([]));
    letters.templates().then(setTemplates).catch(() => setTemplates([]));
    if (!initialMatterRef) loadAnalytics();
  }, [initialMatterRef, loadAnalytics]);

  const openDetail = async (id) => {
    try { setDetail(await esign.get(id)); }
    catch { toast.error('Failed to load eSign detail'); }
  };

  const remind = async (id) => {
    try {
      const r = await esign.remind(id);
      if (r.delivery?.sent) toast.success('Reminder email sent');
      else toast.info(`Link: ${r.link || 'generated'} (email not delivered)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reminder failed'); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this eSign request?')) return;
    try { await esign.cancel(id); toast.success('Cancelled'); load(); setDetail(null); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  const downloadCert = async (id) => {
    try {
      const blob = await esign.certificatePdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esign-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Certificate download failed'); }
  };

  return (
    <div className={embedded ? '' : 'p-6'} data-testid="esign-page">
      {!embedded && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#2ECC9A]/15">
            <FileSignature className="h-4 w-4 text-[#2ECC9A]" />
          </div>
          <div className="flex-1">
            <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">eSign</h1>
            <div className="text-sm text-slate-500">
              {counts.total} requests · <b className="text-amber-700">{counts.pending} pending</b> · <b className="text-emerald-700">{counts.signed} signed</b>
              {' — native document signing with typed name + drawn signature + audit trail.'}
            </div>
          </div>
          <Button data-testid="esign-new-btn" onClick={() => setComposerOpen(true)}
                  className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            <Send className="h-3 w-3" /> Send for eSignature
          </Button>
        </div>
      )}

      {embedded && (
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
            <FileSignature className="h-4 w-4 text-[#2ECC9A]" /> eSign · {counts.total} on this matter
          </div>
          <Button data-testid="esign-new-btn-inline" onClick={() => setComposerOpen(true)} size="sm"
                  className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            <Send className="h-3 w-3" /> Send for eSignature
          </Button>
        </div>
      )}

      {!embedded && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Select value={matterFilter} onValueChange={setMatterFilter}>
            <SelectTrigger data-testid="esign-matter-filter" className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All matters</SelectItem>
              {matters.map((m) => (
                <SelectItem key={m.ref} value={m.ref}>{m.ref} · {m.applicant}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="esign-status-filter" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {!embedded && analytics && <EsignAnalyticsCard data={analytics} onRefresh={loadAnalytics} />}

      {loading ? (
        <div className="mt-8 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card className="mt-4 border-dashed border-slate-200">
          <CardContent className="p-8 text-center text-xs text-slate-500">
            No eSign requests yet. Click <b>Send for eSignature</b> to draft one from a letter template.
          </CardContent>
        </Card>
      ) : (
        <div className="mt-4 overflow-hidden rounded-md border border-slate-200" data-testid="esign-list">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Title</th>
                <th className="px-3 py-2 font-medium">Matter</th>
                <th className="px-3 py-2 font-medium">Signer</th>
                <th className="px-3 py-2 font-medium">Sent</th>
                <th className="px-3 py-2 font-medium">Signed</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const Icon = STATUS_ICON[r.status] || Clock;
                return (
                  <tr key={r.id} data-testid={`esign-row-${r.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <button onClick={() => openDetail(r.id)}
                              data-testid={`esign-title-${r.id}`}
                              className="text-left font-semibold text-[#1A3A5C] hover:text-[#2ECC9A]">
                        {r.title}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.matter_ref}</td>
                    <td className="px-3 py-2 text-slate-600">{r.signer_email}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">
                      {r.sent_at ? new Date(r.sent_at).toLocaleString('en-AU') : '—'}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">
                      {r.signed_at ? new Date(r.signed_at).toLocaleString('en-AU') : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`gap-1 text-[10px] capitalize ${STATUS_TONE[r.status] || ''}`}>
                        <Icon className="h-3 w-3" /> {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {r.status === 'pending' && (
                          <Button data-testid={`esign-remind-${r.id}`} onClick={() => remind(r.id)}
                                  size="sm" variant="outline"
                                  className="h-7 gap-1 border-slate-200 text-[10px]">
                            <RefreshCw className="h-3 w-3" /> Remind
                          </Button>
                        )}
                        {r.status === 'signed' && (
                          <Button data-testid={`esign-cert-${r.id}`} onClick={() => downloadCert(r.id)}
                                  size="sm" variant="outline"
                                  className="h-7 gap-1 border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
                            <Download className="h-3 w-3" /> Certificate
                          </Button>
                        )}
                        {r.status === 'pending' && (
                          <Button data-testid={`esign-cancel-${r.id}`} onClick={() => cancel(r.id)}
                                  size="sm" variant="outline"
                                  className="h-7 gap-1 border-red-200 bg-red-50 text-[10px] text-red-700">
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ComposerDialog
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={() => { setComposerOpen(false); load(); }}
        templates={templates}
        matters={matters}
        defaultMatterRef={initialMatterRef}
      />

      <DetailDialog
        detail={detail}
        onClose={() => setDetail(null)}
        onRemind={remind}
        onCancel={cancel}
        onDownload={downloadCert}
      />
    </div>
  );
}


function ComposerDialog({ open, onClose, onCreated, templates, matters, defaultMatterRef }) {
  const [mode, setMode] = React.useState('template'); // 'template' | 'pdf'
  const [pdfFile, setPdfFile] = React.useState(null);
  const [matterRef, setMatterRef] = React.useState(defaultMatterRef || '');
  const [tplId, setTplId] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [bodyHtml, setBodyHtml] = React.useState('');
  const [signerEmails, setSignerEmails] = React.useState([]); // string[]
  const [emailDraft, setEmailDraft] = React.useState('');
  const [signerName, setSignerName] = React.useState('');
  const [rendering, setRendering] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setMode('template');
      setPdfFile(null);
      setMatterRef(defaultMatterRef || '');
      setTplId(''); setTitle(''); setBodyHtml('');
      setSignerEmails([]); setEmailDraft(''); setSignerName(''); setResult(null);
    }
  }, [open, defaultMatterRef]);

  // Auto-populate signer email from selected matter
  React.useEffect(() => {
    if (!matterRef) return;
    const m = matters.find((x) => x.ref === matterRef);
    if (m) {
      if (signerEmails.length === 0 && m.applicant_email) {
        setSignerEmails([m.applicant_email.toLowerCase()]);
      }
      if (!signerName && m.applicant) setSignerName(m.applicant);
    }
  }, [matterRef, matters]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const addEmail = (raw) => {
    const parts = raw.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const next = [...signerEmails];
    let added = 0;
    for (const p of parts) {
      if (isValidEmail(p) && !next.includes(p)) { next.push(p); added += 1; }
    }
    if (added > 0) setSignerEmails(next);
    setEmailDraft('');
  };

  const removeEmail = (email) => {
    setSignerEmails(signerEmails.filter((e) => e !== email));
  };

  const onEmailKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && emailDraft.trim()) {
      e.preventDefault();
      addEmail(emailDraft);
    } else if (e.key === 'Backspace' && !emailDraft && signerEmails.length > 0) {
      // Backspace on empty input removes the last chip — matches Gmail's UX
      removeEmail(signerEmails[signerEmails.length - 1]);
    }
  };

  const renderTpl = async () => {
    if (!tplId || !matterRef) return;
    setRendering(true);
    try {
      const r = await letters.render(tplId, matterRef);
      setTitle(r.title);
      setBodyHtml(r.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Render failed');
    } finally { setRendering(false); }
  };

  const send = async () => {
    // Merge any un-added draft (user typed but didn't hit Enter)
    let emails = signerEmails;
    if (emailDraft.trim() && isValidEmail(emailDraft.trim())) {
      const d = emailDraft.trim().toLowerCase();
      if (!emails.includes(d)) emails = [...emails, d];
    }
    if (!matterRef || emails.length === 0) return;
    if (mode === 'pdf') {
      if (!pdfFile || !title.trim()) return;
    } else if (!title.trim() || !bodyHtml.trim()) return;
    setSending(true);
    try {
      if (mode === 'pdf') {
        // PDF upload path — one-at-a-time, batch-loop for multi-signer
        const rows = [];
        for (const em of emails) {
          const r = await esign.uploadPdf({
            matter_ref: matterRef, title: title.trim(),
            signer_email: em,
            signer_name: signerName.trim() || null,
            pdf: pdfFile,
          });
          rows.push({ signer_email: em, ok: true, esign_id: r.id,
                      sent: r.signer_link?.sent, link: r.signer_link?.link });
        }
        setResult({ mode: 'bulk', payload: { items: rows, total: rows.length, ok: rows.length, sent: rows.filter((x) => x.sent).length } });
        const sent = rows.filter((x) => x.sent).length;
        if (sent === rows.length) toast.success(`Sent PDF to ${sent} signer${sent === 1 ? '' : 's'}`);
        else toast.info(`Created ${rows.length}/${emails.length} · ${sent} emailed`);
        setTimeout(() => { if (sent === rows.length) onCreated(); }, sent === rows.length ? 1400 : 0);
      } else if (emails.length === 1) {
        const r = await esign.create({
          matter_ref: matterRef, title: title.trim(), body_html: bodyHtml,
          signer_email: emails[0], signer_name: signerName.trim() || null,
          template_id: tplId || null,
        });
        setResult({ mode: 'single', payload: r });
        if (r.signer_link?.sent) toast.success('eSignature request sent by email');
        else toast.info('Request created — copy the link below');
        setTimeout(() => { if (r.signer_link?.sent) onCreated(); }, r.signer_link?.sent ? 1200 : 0);
      } else {
        const r = await esign.bulkCreate({
          matter_ref: matterRef, title: title.trim(), body_html: bodyHtml,
          signer_emails: emails, template_id: tplId || null,
        });
        setResult({ mode: 'bulk', payload: r });
        if (r.sent === r.total) toast.success(`Sent to ${r.sent} signers`);
        else toast.info(`Created ${r.ok}/${r.total} · ${r.sent} emailed, ${r.ok - r.sent} link-only`);
        setTimeout(() => { if (r.sent === r.total) onCreated(); }, r.sent === r.total ? 1400 : 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="esign-composer" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A3A5C]">
            <FileSignature className="h-4 w-4 text-[#2ECC9A]" /> Send document for eSignature
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 text-xs md:grid-cols-2">
          <div className="md:col-span-2 flex items-center gap-1 rounded-md border border-slate-200 bg-white p-1" data-testid="esign-composer-mode">
            <button
              type="button"
              data-testid="esign-composer-mode-template"
              onClick={() => setMode('template')}
              className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition ${
                mode === 'template' ? 'bg-[#1A3A5C] text-white' : 'text-slate-500 hover:text-[#1A3A5C]'
              }`}
            >
              From letter template
            </button>
            <button
              type="button"
              data-testid="esign-composer-mode-pdf"
              onClick={() => setMode('pdf')}
              className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition ${
                mode === 'pdf' ? 'bg-[#1A3A5C] text-white' : 'text-slate-500 hover:text-[#1A3A5C]'
              }`}
            >
              Upload PDF
            </button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Matter</label>
            <Select value={matterRef} onValueChange={setMatterRef}>
              <SelectTrigger data-testid="esign-composer-matter" className="mt-1 h-9 text-xs">
                <SelectValue placeholder="Select a matter…" />
              </SelectTrigger>
              <SelectContent>
                {matters.map((m) => (
                  <SelectItem key={m.ref} value={m.ref}>{m.ref} · {m.applicant}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {mode === 'template' && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Letter template (optional)</label>
            <div className="mt-1 flex gap-1">
              <Select value={tplId} onValueChange={setTplId}>
                <SelectTrigger data-testid="esign-composer-template" className="h-9 text-xs">
                  <SelectValue placeholder="Pick a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="esign-composer-render" onClick={renderTpl}
                      disabled={!tplId || !matterRef || rendering}
                      variant="outline" size="sm" className="h-9 gap-1 border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[#0F766E] text-xs">
                {rendering ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Render'}
              </Button>
            </div>
          </div>
          )}
          {mode === 'pdf' && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">PDF file (max 15 MB)</label>
            <label
              data-testid="esign-composer-pdf-drop"
              className="mt-1 flex h-9 cursor-pointer items-center gap-2 rounded-md border border-dashed border-[#2ECC9A]/50 bg-[#2ECC9A]/5 px-3 text-xs text-[#0F766E] hover:bg-[#2ECC9A]/10"
            >
              <Upload className="h-3.5 w-3.5" />
              <span className="flex-1 truncate">
                {pdfFile ? `${pdfFile.name} · ${Math.round(pdfFile.size / 1024)} KB` : 'Click or drop PDF here'}
              </span>
              <input
                type="file"
                accept="application/pdf"
                data-testid="esign-composer-pdf-input"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </div>
          )}
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">
              Signer email{signerEmails.length > 1 && ` · ${signerEmails.length} signers (bulk)`}
            </label>
            <div
              data-testid="esign-composer-emails"
              className="mt-1 flex flex-wrap items-center gap-1 rounded-md border border-slate-200 bg-white p-1.5 min-h-[36px]"
              onClick={(e) => e.currentTarget.querySelector('input')?.focus()}
            >
              {signerEmails.map((em) => (
                <span
                  key={em}
                  data-testid={`esign-composer-chip-${em}`}
                  className="inline-flex items-center gap-1 rounded-full bg-[#2ECC9A]/10 px-2 py-0.5 text-[11px] text-[#0F766E]"
                >
                  {em}
                  <button
                    type="button"
                    onClick={() => removeEmail(em)}
                    data-testid={`esign-composer-chip-remove-${em}`}
                    className="rounded-full hover:bg-[#2ECC9A]/20"
                    aria-label={`Remove ${em}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                data-testid="esign-composer-email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={onEmailKeyDown}
                onBlur={() => emailDraft.trim() && addEmail(emailDraft)}
                placeholder={signerEmails.length === 0 ? 'applicant@example.com  (press Enter to add — send to multiple for bulk)' : 'Add another…'}
                type="email"
                className="min-w-[180px] flex-1 border-0 bg-transparent p-0.5 text-xs outline-none focus:ring-0"
              />
            </div>
            <div className="mt-1 text-[10px] text-slate-400">
              Tip: paste comma-separated emails, hit Enter, or send to a whole family in one click.
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Signer name (optional · used only for the first signer when in single mode)</label>
            <Input data-testid="esign-composer-signer-name" value={signerName}
                   onChange={(e) => setSignerName(e.target.value)}
                   className="mt-1 h-9 text-xs" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Document title</label>
            <Input data-testid="esign-composer-title" value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   placeholder="e.g. Client engagement letter"
                   className="mt-1 h-9 text-xs" />
          </div>
          {mode === 'template' && (
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Document body (HTML)</label>
            <textarea data-testid="esign-composer-body" value={bodyHtml}
                      onChange={(e) => setBodyHtml(e.target.value)} rows={6}
                      placeholder="Paste or render a template above…"
                      className="mt-1 w-full rounded-md border border-slate-200 bg-white p-2 font-mono text-[11px]" />
            {bodyHtml && (
              <details className="mt-2 rounded-md border border-slate-100">
                <summary className="cursor-pointer bg-slate-50 px-2 py-1 text-[10px] text-slate-500 hover:text-[#1A3A5C]">
                  Preview
                </summary>
                <div className="max-h-64 overflow-auto p-3 text-xs" data-testid="esign-composer-preview"
                     dangerouslySetInnerHTML={{ __html: safeHtml(bodyHtml) }} />
              </details>
            )}
          </div>
          )}
          {result && result.mode === 'single' && (
            <div data-testid="esign-composer-result"
                 className={`md:col-span-2 rounded-md p-2 text-[11px] ${
                   result.payload.signer_link?.sent ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'
                 }`}>
              {result.payload.signer_link?.sent ? (
                <>Email sent to <b>{result.payload.signer_email}</b>. Closing…</>
              ) : (
                <div className="space-y-1">
                  <div>Email provider not configured — copy this link and send it to your client manually:</div>
                  <div className="flex items-center gap-1">
                    <code className="flex-1 truncate rounded bg-white px-2 py-1 text-[10px]">
                      {result.payload.signer_link?.link}
                    </code>
                    <Button size="sm" variant="outline" data-testid="esign-composer-copy"
                            onClick={() => { navigator.clipboard.writeText(result.payload.signer_link?.link || ''); toast.success('Link copied'); }}
                            className="h-7 gap-1 border-slate-200 text-[10px]">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {result && result.mode === 'bulk' && (
            <div data-testid="esign-composer-bulk-result"
                 className={`md:col-span-2 rounded-md p-2 text-[11px] ${
                   result.payload.sent === result.payload.total ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'
                 }`}>
              <div className="mb-1">
                <b>{result.payload.ok}/{result.payload.total}</b> requests created ·
                <b> {result.payload.sent}</b> emailed
                {result.payload.ok - result.payload.sent > 0 && `, ${result.payload.ok - result.payload.sent} link-only`}.
              </div>
              <div className="space-y-1">
                {result.payload.items.map((it) => (
                  <div key={it.signer_email} className="flex items-center gap-1 text-[10px]">
                    <span className="w-56 truncate font-mono">{it.signer_email}</span>
                    {it.sent ? (
                      <span className="text-emerald-700">✓ emailed</span>
                    ) : it.link ? (
                      <>
                        <code className="flex-1 truncate rounded bg-white px-2 py-0.5">{it.link}</code>
                        <Button size="sm" variant="outline"
                                onClick={() => { navigator.clipboard.writeText(it.link); toast.success('Copied'); }}
                                className="h-6 gap-1 border-slate-200 text-[9px]">
                          <Copy className="h-2.5 w-2.5" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-red-600">✕ {it.error || 'failed'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={sending} className="h-8 text-xs">Cancel</Button>
          <Button data-testid="esign-composer-send" onClick={send} size="sm"
                  disabled={
                    sending || !matterRef || !title.trim() ||
                    (signerEmails.length === 0 && !emailDraft.trim()) ||
                    (mode === 'template' && !bodyHtml.trim()) ||
                    (mode === 'pdf' && !pdfFile)
                  }
                  className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : (
              <><Send className="h-3 w-3" /> Send{signerEmails.length > 1 ? ` to ${signerEmails.length}` : ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DetailDialog({ detail, onClose, onRemind, onCancel, onDownload }) {
  if (!detail) return null;
  const isSigned = detail.status === 'signed';
  return (
    <Dialog open={!!detail} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="esign-detail" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A3A5C]">
            <FileSignature className="h-4 w-4 text-[#2ECC9A]" /> {detail.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="grid gap-1 text-[11px] text-slate-500">
            <div>Matter <span className="font-mono text-slate-700">{detail.matter_ref}</span></div>
            <div>Signer <b className="text-slate-700">{detail.signer_email}</b> ({detail.signer_name || '—'})</div>
            <div>Created {new Date(detail.created_at).toLocaleString('en-AU')} by {detail.created_by_name}</div>
            {detail.sent_at && <div>Emailed {new Date(detail.sent_at).toLocaleString('en-AU')}</div>}
            {detail.opened_at && <div>Opened by client {new Date(detail.opened_at).toLocaleString('en-AU')}</div>}
            {isSigned && (
              <>
                <div>Signed {new Date(detail.signed_at).toLocaleString('en-AU')}</div>
                <div>By <b>{detail.signed_name}</b> · IP {detail.signed_ip}</div>
                <div className="truncate" title={detail.signed_user_agent}>UA {detail.signed_user_agent}</div>
              </>
            )}
          </div>
          <details open>
            <summary className="cursor-pointer rounded-md bg-slate-50 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">Body</summary>
            {detail.source_type === 'pdf' && detail.source_pdf_gridfs_id ? (
              <div className="mt-2" data-testid="esign-detail-source-pdf">
                <iframe
                  src={esign.sourcePdfUrl(detail.id)}
                  className="h-96 w-full rounded-md border border-slate-200"
                  title="Uploaded PDF"
                />
                <div className="mt-1 text-[10px] text-slate-500">
                  Uploaded PDF · {detail.source_pdf_filename}
                </div>
              </div>
            ) : (
              <div className="mt-2 max-h-72 overflow-auto rounded-md border border-slate-200 p-3"
                   data-testid="esign-detail-body"
                   dangerouslySetInnerHTML={{ __html: safeHtml(detail.body_html) }} />
            )}
          </details>
          {isSigned && detail.signature_data_url && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Captured signature</div>
              <div className="mt-1 rounded-md border border-dashed border-[#2ECC9A]/40 bg-[#2ECC9A]/5 p-3 text-center">
                <img src={detail.signature_data_url} alt="Signature" className="mx-auto max-h-24"
                     data-testid="esign-detail-signature" />
                <div className="mt-2 font-serif text-lg text-[#1A3A5C]">{detail.signed_name}</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-1">
          {detail.status === 'pending' && (
            <>
              <Button data-testid="esign-detail-remind" onClick={() => onRemind(detail.id)}
                      variant="outline" size="sm" className="h-8 gap-1 text-xs">
                <RefreshCw className="h-3 w-3" /> Remind
              </Button>
              <Button data-testid="esign-detail-cancel" onClick={() => onCancel(detail.id)}
                      variant="outline" size="sm"
                      className="h-8 gap-1 border-red-200 bg-red-50 text-red-700 text-xs">
                <X className="h-3 w-3" /> Cancel
              </Button>
            </>
          )}
          {isSigned && (
            <Button data-testid="esign-detail-cert" onClick={() => onDownload(detail.id)} size="sm"
                    className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
              <Download className="h-3 w-3" /> Certificate PDF
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EsignAnalyticsCard({ data, onRefresh }) {
  const t = data.totals || {};
  const tts = data.time_to_sign || {};
  const rem = data.reminders || {};
  const tpls = data.templates || [];
  const weekly = data.weekly || [];
  const [downloading, setDownloading] = React.useState(false);
  const fmtHours = (h) => {
    if (h === null || h === undefined) return '—';
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${h}h`;
    return `${Math.round(h / 24)}d`;
  };
  const maxWeekly = Math.max(1, ...weekly.map((w) => Math.max(w.sent, w.signed)));
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await esign.analyticsPdf(data.range_days || 90);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esign-analytics-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Analytics report downloaded');
    } catch { toast.error('PDF export failed'); }
    finally { setDownloading(false); }
  };
  return (
    <Card className="mt-4 border-[#2ECC9A]/30 bg-gradient-to-br from-[#2ECC9A]/5 to-white" data-testid="esign-analytics">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
            <BarChart3 className="h-4 w-4 text-[#2ECC9A]" /> eSign analytics · last {data.range_days || 90} days
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="esign-analytics-pdf"
              onClick={downloadPdf}
              disabled={downloading}
              size="sm"
              variant="outline"
              className="h-7 gap-1 border-[#1A3A5C]/20 bg-white text-[10px] text-[#1A3A5C] hover:bg-[#1A3A5C]/5"
            >
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Download className="h-3 w-3" /> Board-ready PDF</>}
            </Button>
            <button onClick={onRefresh} data-testid="esign-analytics-refresh"
                    className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#1A3A5C]">
              <RefreshCw className="h-3 w-3" /> refresh
            </button>
          </div>
        </div>
        <div className="mt-3 grid gap-2 md:grid-cols-4">
          <div className="rounded-md bg-white border border-slate-200 p-3" data-testid="esign-analytics-completion">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Completion
            </div>
            <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">
              {t.completion_rate_pct}<span className="text-sm text-slate-400">%</span>
            </div>
            <div className="text-[10px] text-slate-500">
              {t.signed}/{t.total} signed · {t.pending} pending
            </div>
          </div>
          <div className="rounded-md bg-white border border-slate-200 p-3" data-testid="esign-analytics-tts">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <Timer className="h-3 w-3 text-[#2ECC9A]" /> Avg time-to-sign
            </div>
            <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">{fmtHours(tts.avg_hours)}</div>
            <div className="text-[10px] text-slate-500">
              median {fmtHours(tts.median_hours)} · n={tts.sample_size || 0}
            </div>
          </div>
          <div className="rounded-md bg-white border border-slate-200 p-3" data-testid="esign-analytics-reminders">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <RefreshCw className="h-3 w-3 text-amber-500" /> Reminder-driven
            </div>
            <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">
              {rem.reminder_driven_pct}<span className="text-sm text-slate-400">%</span>
            </div>
            <div className="text-[10px] text-slate-500">
              {rem.reminder_driven_signs} of {t.signed} signs needed a nudge
            </div>
          </div>
          <div className="rounded-md bg-white border border-slate-200 p-3" data-testid="esign-analytics-cancelled">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <X className="h-3 w-3 text-slate-400" /> Cancelled
            </div>
            <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">{t.cancelled}</div>
            <div className="text-[10px] text-slate-500">documents pulled back</div>
          </div>
        </div>

        {weekly.length > 0 && (
          <div className="mt-4">
            <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <TrendingUp className="h-3 w-3" /> Weekly cadence
            </div>
            <div className="flex items-end gap-2 rounded-md bg-white border border-slate-100 p-3" style={{ height: 80 }}>
              {weekly.slice(-12).map((w) => (
                <div key={w.week} className="flex flex-1 flex-col items-center gap-0.5" data-testid={`esign-analytics-week-${w.week}`}>
                  <div className="flex h-14 w-full items-end gap-0.5">
                    <div className="flex-1 rounded-t bg-slate-200"
                         style={{ height: `${(w.sent / maxWeekly) * 100}%` }}
                         title={`Sent: ${w.sent}`} />
                    <div className="flex-1 rounded-t bg-[#2ECC9A]"
                         style={{ height: `${(w.signed / maxWeekly) * 100}%` }}
                         title={`Signed: ${w.signed}`} />
                  </div>
                  <div className="truncate text-[9px] text-slate-400">{w.week.slice(5)}</div>
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-end gap-3 text-[9px] text-slate-500">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300" /> Sent</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#2ECC9A]" /> Signed</span>
            </div>
          </div>
        )}

        {tpls.length > 0 && (
          <div className="mt-4">
            <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
              <Award className="h-3 w-3 text-amber-500" /> Fastest-signed templates
            </div>
            <div className="overflow-hidden rounded-md border border-slate-100 bg-white">
              <table className="w-full text-xs" data-testid="esign-analytics-templates">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-3 py-1.5 font-medium">Template</th>
                    <th className="px-3 py-1.5 font-medium text-right">Signed</th>
                    <th className="px-3 py-1.5 font-medium text-right">Avg to sign</th>
                  </tr>
                </thead>
                <tbody>
                  {tpls.slice(0, 5).map((tp) => (
                    <tr key={tp.template_id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 text-[#1A3A5C]">{tp.template_title || tp.template_id}</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{tp.signed_count}</td>
                      <td className="px-3 py-1.5 text-right font-medium text-[#0F766E]">{fmtHours(tp.avg_hours_to_sign)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
